import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fillTrendBuckets } from "@/lib/dashboardDateRange";
import { formatInt, formatUsd } from "@/lib/statsFormat";
import type { CallDashboardStats } from "@/services/callStatsService";

type Metric = "calls" | "minutes" | "cost";

const METRICS: { value: Metric; label: string }[] = [
  { value: "calls", label: "Calls" },
  { value: "minutes", label: "Minutes" },
  { value: "cost", label: "Cost" },
];

const LABEL_FORMAT = { hour: "HH:mm", day: "MMM d", week: "MMM d, yyyy" } as const;

interface TrendChartProps {
  stats: CallDashboardStats;
  // Start the chart at the first bucket that has calls, not at the range start.
  fromFirstCall?: boolean;
}

const TrendChart = ({ stats, fromFirstCall = false }: TrendChartProps) => {
  const [metric, setMetric] = useState<Metric>("calls");
  const { range, trend } = stats;

  const data = useMemo(() => {
    const filled = fillTrendBuckets(
      trend,
      fromFirstCall && trend.length > 0
        ? parseISO(trend[0].bucket)
        : new Date(range.from),
      new Date(range.to),
      range.granularity,
    );
    return filled.map((point) => ({
      label: format(parseISO(point.bucket), LABEL_FORMAT[range.granularity]),
      calls: point.calls,
      minutes: Math.round(point.seconds / 60),
      cost: point.cost_total,
    }));
  }, [trend, range, fromFirstCall]);

  const formatValue = (value: number) =>
    metric === "cost" ? formatUsd(value) : formatInt(value);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Trend</CardTitle>
        <div className="flex gap-1">
          {METRICS.map((item) => (
            <Button
              key={item.value}
              size="sm"
              variant={metric === item.value ? "default" : "outline"}
              onClick={() => setMetric(item.value)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="var(--chart-axis)"
                fontSize={12}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                stroke="var(--chart-axis)"
                fontSize={12}
                width={56}
                allowDecimals={metric === "cost"}
                tickFormatter={formatValue}
              />
              <Tooltip
                cursor={{ fill: "var(--muted)" }}
                formatter={(value: number) => [formatValue(value), METRICS.find((m) => m.value === metric)?.label]}
                contentStyle={{
                  background: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                }}
              />
              <Bar dataKey={metric} fill="var(--chart-1)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendChart;
